import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import { US } from '../../constants/country-codes';
import GetProviderDisplayName from '../../components/data-driven-logo/get-provider-display-name';
import { OpenSierraSource, useSupportChat } from '../../global/support-chat/use-support-chat';
import { LocalStorageService } from '../../services/local-storage-service';
import { generatePersisentKey } from '../../hooks/use-local-storage';
import history from '../../history';

import { messages } from './index';

const ApprovalRequestedSupportChatLink = ({ children }) => {
  const { isEnabled, openSupportChat } = useSupportChat();
  const handleClick = useCallback(() => {
    openSupportChat(OpenSierraSource.WalletApprovalNotification);
  }, [openSupportChat]);

  if (!isEnabled) {
    return children;
  }

  return (
    <Button onClick={handleClick} theme='link'>
      {children}
    </Button>
  );
};

export const pendingApprovalProviderIdsKey = generatePersisentKey('providerIdsWaitingApproval');

export const getProviderIdFromPendingApprovalCache = () => {
  return LocalStorageService.getItem(pendingApprovalProviderIdsKey) || [];
};

const clearPendingProviderIdsFromCache = (providerId) => {
  const providerIdsWaitingApproval = getProviderIdFromPendingApprovalCache();
  const providerIdsStillWaitingApproval = providerIdsWaitingApproval.filter((id) => id !== providerId);

  if (providerIdsStillWaitingApproval.length === 0) {
    LocalStorageService.removeItem(pendingApprovalProviderIdsKey);
    return;
  }

  LocalStorageService.setItem(pendingApprovalProviderIdsKey, providerIdsStillWaitingApproval);
};

const addProvidersToPendingApprovalCache = (providerIds) => {
  const providerIdsWaitingApproval = getProviderIdFromPendingApprovalCache();

  if (!providerIdsWaitingApproval || providerIdsWaitingApproval.length === 0) {
    LocalStorageService.setItem(pendingApprovalProviderIdsKey, providerIds);
    return;
  }

  providerIdsWaitingApproval.push(...providerIds);

  LocalStorageService.setItem(pendingApprovalProviderIdsKey, providerIdsWaitingApproval);
};

export const showProviderApprovalRequestedNotification = (providerIds, sellerHomeCountry) => {
  if (sellerHomeCountry !== US || !providerIds?.length) {
    return;
  }

  addProvidersToPendingApprovalCache(providerIds);

  const notificationConfig = {
    title: <Text textType={TextType.H3} value={messages.approvalRequestedNotificationTitle} />,
    content: (
      <Text
        formattedMessageValues={{
          link: (chunks) => <ApprovalRequestedSupportChatLink>{chunks}</ApprovalRequestedSupportChatLink>,
        }}
        textType={TextType.Description}
        value={messages.approvalRequestedNotificationContent}
      />
    ),
    notificationType: NOTIFICATION_TYPE_SUCCESS,
    duration: NOTIFICATION_DURATIONS.MEDIUM,
  };

  NotificationService.showNotification(notificationConfig);
};

export const showWalletProviderApprovalGrantedNotification = async (providerId, sellerHomeCountry, updateStateFn = noop) => {
  if (sellerHomeCountry !== US) {
    return;
  }

  clearPendingProviderIdsFromCache(providerId);
  updateStateFn();

  const sanitizeBranding = true;
  const nameDescriptor = GetProviderDisplayName(providerId, sanitizeBranding);
  const name = <FormattedMessage {...nameDescriptor} />;

  const notificationConfig = {
    title: <Text formattedMessageValues={{ carrierName: name }} textType={TextType.H3} value={messages.approvalGrantedNotificationTitle} />,
    content: (
      <Text
        formattedMessageValues={{ carrierName: name }}
        textType={TextType.Description}
        value={messages.approvalGrantedNotificationContent}
      />
    ),
    buttons: [
      {
        text: <FormattedMessage {...messages.approvalGrantedCallToAction} />,
        theme: 'primary',
        callback: () => {
          history.push('/orders');
        },
      },
      {
        text: <FormattedMessage {...messages.approvalGrantedManageCarriers} />,
        theme: 'link',
        callback: () => {
          history.push('/settings/carriers');
        },
      },
    ],
    notificationType: NOTIFICATION_TYPE_SUCCESS,
    duration: NOTIFICATION_DURATIONS.MEDIUM,
  };

  NotificationService.showNotification(notificationConfig);
};
